import type { ReactNode } from 'react';
import type { TextInputStringProps } from '../TextInput/TextInput.types';

export type SearchEvent = {
  inputValue: string;
  nextPageNumber: number;
};

export type SearchResult<TOption> = {
  currentPage: number;
  paginationLimit: number;
  totalPageCount: number | null;
  totalRowsCount: number | null;
  content: TOption[] | null;
};

export type BaseAutocompleteInputProps<
  TOption extends string | Record<string, any>,
> = Omit<TextInputStringProps, 'onChange' | 'value' | 'type'> & {
  getOptionLabel?: (option: TOption) => string;
  isOptionEqualToValue?: (option: TOption, value: string) => boolean;
  menuLabel?: string;
  renderMenuHeader?: ReactNode;
  onChange: (value: string) => void;
  onOptionSelect?: (option: TOption) => void;
  onSearch: (e: SearchEvent) => Promise<SearchResult<TOption>>;
  options: TOption[] | null;
  value: string;
  inputId?: string;
  renderOption?: (option: TOption) => ReactNode;
  getOptionKey?: (option: TOption, index: number) => string;
  hideClearButton?: boolean;
  hideMenuWhenEmpty?: boolean;
  keepMenuOpenOnSelect?: boolean;
};

// options-related functions are handled internally for string options
export type AutocompleteInputStringProps = Omit<
  BaseAutocompleteInputProps<string>,
  'getOptionLabel' | 'isOptionEqualToValue' | 'onOptionSelect'
>;

export type AutocompleteInputRecordProps<TOption extends Record<string, any>> =
  Omit<
    BaseAutocompleteInputProps<TOption>,
    'getOptionLabel' | 'isOptionEqualToValue' | 'onOptionSelect'
  > & {
    // make options-related functions mandatory for record options
    getOptionLabel: (option: TOption) => string;
    isOptionEqualToValue: (option: TOption, value: string) => boolean;
    onOptionSelect: (option: TOption) => void;
  };

export function isStringAutocompleteOption<
  TOption extends string | Record<string, any>,
>(option: TOption): option is Extract<TOption, string> {
  return typeof option === 'string';
}
